import React from 'react';
import Card from 'components/card';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  PaginationState,
  ColumnDef,
} from '@tanstack/react-table';
import {
  keepPreviousData,
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';

import Pagination from 'components/pagination';
import Loader from 'components/loader';
import FilterComponent from './filters';
import { IFilter } from 'types';
import Button from 'components/button';

const queryClient = new QueryClient();

interface IProps {
  columns: ColumnDef<any, string>[];
  fetchData: any;
  header: string;
  filters?: IFilter[];
}
const ComplexTable: React.FC<IProps> = (props) => {
  const { columns, fetchData, header, filters } = props;
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [filtersState, setFiltersState] = React.useState<{
    [key: string]: string;
  }>({});

  const dataQuery = useQuery({
    queryKey: ['data', pagination, filtersState],
    queryFn: () => fetchData(pagination, filtersState),
    placeholderData: keepPreviousData,
  });

  const defaultData = React.useMemo(() => [], []);
  const table = useReactTable({
    data: dataQuery.data?.rows ?? defaultData,
    columns,

    pageCount: dataQuery?.data?.pageCount ?? 1,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  const clearFilters = () => {
    const clearedFilters = Object.keys(filtersState).reduce((acc: any, key) => {
      acc[key] = '';
      return acc;
    }, {});
    setFiltersState(clearedFilters);
  };

  return (
    <Card extra={'w-full h-full px-6 pb-6 sm:overflow-x-auto min-h-[50vh]'}>
      {header && (
        <div className="relative flex items-center justify-between pt-4">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            {header}
          </div>
        </div>
      )}

      {!dataQuery.isLoading && !dataQuery.isError && filters && (
        <div className="mb-[-10px] flex  flex-wrap items-end gap-4 pt-4">
          {filters?.map((filter, index) => (
            <FilterComponent
              key={filter.key + index}
              filter={filter}
              value={filtersState[filter.key]}
              onChange={(value) => {
                setFiltersState((prev) => ({ ...prev, [filter.key]: value }));
              }}
            />
          ))}
          <Button
            onClick={clearFilters}
            variant="gray"
            extraClasses="mb-2 ml-auto"
          >
            Clear
          </Button>
        </div>
      )}
      {dataQuery.isLoading ? (
        <Loader />
      ) : dataQuery?.data?.rows?.length > 0 ? (
        <>
          <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden ">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="!border-px !border-gray-400"
                  >
                    {headerGroup.headers.map((header) => {
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          onClick={header.column.getToggleSortingHandler()}
                          className="cursor-pointer border-b-[1px] border-gray-200 pb-2 pr-4 pt-4 text-start"
                        >
                          <div className="items-center justify-between text-lg text-brand-600 dark:text-white">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => {
                  return (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <td
                            key={cell.id}
                            className="min-w-[150px] border-white/0 py-3  pr-4 text-lg font-medium text-navy-700 dark:text-white"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="h-2" />
        </>
      ) : (
        <p className="mt-4 text-lg font-semibold text-navy-800">
          No Data to Display
        </p>
      )}
      <Pagination table={table} extraClasses="mt-auto" />
    </Card>
  );
};
const TableComponent = (props: {
  columns: ColumnDef<any, any>[];
  fetchData: any;
  header?: string;
  filters?: IFilter[];
}) => {
  const { columns, fetchData, header, filters } = props;
  return (
    <QueryClientProvider client={queryClient}>
      <ComplexTable
        columns={columns}
        fetchData={fetchData}
        header={header}
        filters={filters}
      />
    </QueryClientProvider>
  );
};
export default TableComponent;
